import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { 
  ArrowRight, 
  Buildings, 
  HouseSimple, 
  CurrencyDollar, 
  Gavel, 
  Globe, 
  CaretLeft, 
  CaretRight,
  ForkKnife,
  Briefcase,
  Storefront,
  GraduationCap,
  Heart,
  ShoppingCart,
  Truck,
  Wrench,
  Lightning,
  Star,
  House,
  Scales,
  Airplane
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon mapping
const ICON_MAP = {
  'ForkKnife': ForkKnife,
  'Buildings': Buildings,
  'CurrencyDollar': CurrencyDollar,
  'Scales': Scales,
  'Airplane': Airplane,
  'Briefcase': Briefcase,
  'Storefront': Storefront,
  'GraduationCap': GraduationCap,
  'Heart': Heart,
  'ShoppingCart': ShoppingCart,
  'Truck': Truck,
  'Wrench': Wrench,
  'Lightning': Lightning,
  'Star': Star,
  'House': House,
  'HouseSimple': HouseSimple,
  'Globe': Globe,
  'Gavel': Gavel
};

// Default hero images for services
const SERVICE_IMAGES = {
  'restaurants': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1920&q=80',
  'real-estate': 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80',
  'finance': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80',
  'advocacy': 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1920&q=80',
  'import-export': 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80'
};

const getServiceImage = (serviceId) => {
  return SERVICE_IMAGES[serviceId] || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80';
};

export default function Home() {
  const { language, t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [services, setServices] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    fetchServices();
    fetchSiteSettings();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API}/services-list`);
      if (res.ok) {
        const data = await res.json();
        setServices(data.filter(s => s.visible && s.featured));
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const res = await fetch(`${API}/site-settings`);
      if (res.ok) setSiteSettings(await res.json());
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const siteName = siteSettings?.site_name || 'SP BD Group';

  // Build hero slides from services
  const heroSlides = services.length > 0 ? services.map((svc, idx) => ({
    title: svc.name,
    subtitle: svc.short_name || svc.name,
    description: svc.description,
    image: getServiceImage(svc.service_id),
    icon: ICON_MAP[svc.icon] || Briefcase,
    link: svc.path,
    accent: svc.color || (idx % 2 === 0 ? '#6BBE45' : '#DC2626')
  })) : [
    {
      title: 'Restaurant Business',
      subtitle: 'Culinary Excellence',
      description: 'Experience world-class dining with our premium restaurant portfolio',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1920&q=80',
      icon: Buildings,
      link: '/restaurants',
      accent: '#6BBE45'
    }
  ];

  const goToSlide = useCallback((index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, heroSlides.length, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  }, [currentSlide, heroSlides.length, goToSlide]);

  // Auto-advance slideshow
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [nextSlide, heroSlides.length]);

  const CurrentIcon = heroSlides[currentSlide]?.icon || Briefcase;
  const currentSlideData = heroSlides[currentSlide] || heroSlides[0];

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO page="home" lang={language} />
      
      {/* Hero Slideshow Section */}
      <section 
        className="relative h-[85vh] flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
        aria-label="Featured Services Showcase"
      >
        {/* Background Slides */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 z-0'
            }`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center transform scale-105 transition-transform duration-[8000ms] ease-out"
              style={{ 
                backgroundImage: `url(${slide.image})`,
                transform: index === currentSlide ? 'scale(1.05)' : 'scale(1)'
              }}
            />
          </div>
        ))}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40 z-10"></div>

        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            {/* Business Badge */}
            <div 
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6 transition-all duration-500"
              style={{ backgroundColor: `${currentSlideData.accent}20`, borderColor: `${currentSlideData.accent}40`, borderWidth: '1px' }}
            >
              <CurrentIcon size={18} weight="fill" style={{ color: currentSlideData.accent }} />
              <span className="text-sm font-bold" style={{ color: currentSlideData.accent }}>
                {currentSlideData.subtitle}
              </span>
            </div>

            {/* Main Title */}
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-none mb-4 transition-all duration-500"
              data-testid="hero-title"
            >
              <span className="block text-white/90 text-lg sm:text-xl font-medium mb-2">{siteName}</span>
              {currentSlideData.title}
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg text-white/85 mb-8 leading-relaxed max-w-xl transition-all duration-500">
              {currentSlideData.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link 
                to={currentSlideData.link}
                className="px-8 py-4 text-white font-bold rounded-sm transition-all duration-300 inline-flex items-center space-x-2 hover:scale-105"
                style={{ backgroundColor: currentSlideData.accent }}
                data-testid="hero-explore-button"
              >
                <span>{t('home.exploreService')} {currentSlideData.title}</span>
                <ArrowRight size={20} weight="bold" />
              </Link>
              <Link 
                to="/contact" 
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold rounded-sm border border-white/30 hover:bg-white/20 transition-all duration-300"
                data-testid="hero-contact-button"
              >
                {t('home.getInTouch')}
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        {heroSlides.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 border border-white/20"
              data-testid="slider-prev"
              aria-label="Previous slide"
            >
              <CaretLeft size={24} weight="bold" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 border border-white/20"
              data-testid="slider-next"
              aria-label="Next slide"
            >
              <CaretRight size={24} weight="bold" />
            </button>
          </>
        )}

        {/* Slide Indicators */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center space-x-3">
            {heroSlides.map((slide, index) => (
              <button
                key={`slide-indicator-${slide.id || index}`}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentSlide 
                    ? 'w-10 h-3' 
                    : 'w-3 h-3 hover:bg-white/60'
                }`}
                style={{ 
                  backgroundColor: index === currentSlide ? currentSlideData.accent : 'rgba(255,255,255,0.4)'
                }}
                data-testid={`slide-indicator-${index}`}
                aria-label={`Go to slide ${index + 1}: ${slide.title}`}
              />
            ))}
          </div>
        )}

        {/* Progress Bar */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10 z-30">
            <div 
              className="h-full transition-all duration-300"
              style={{ 
                width: `${((currentSlide + 1) / heroSlides.length) * 100}%`,
                backgroundColor: currentSlideData.accent
              }}
            />
          </div>
        )}
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">{t('home.ourBusinesses')}</h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              {t('home.ourBusinessesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="business-grid">
            {services.map((service, index) => {
              const Icon = ICON_MAP[service.icon] || Briefcase;
              const image = getServiceImage(service.service_id);
              return (
                <Link
                  key={service.service_id}
                  to={service.path}
                  className="group relative overflow-hidden border border-gray-200 rounded-sm bg-white hover:-translate-y-1 transition-all duration-300 hover:shadow-lg"
                  data-testid={`business-card-${service.service_id}`}
                  style={{ height: index < 2 ? '400px' : '320px' }}
                >
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundImage: `url(${image})` }}
                  ></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
                  <div className="relative h-full p-8 flex flex-col justify-end">
                    <div className="mb-4" style={{ color: service.color }}>
                      <Icon size={48} weight="duotone" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-white mb-3">{service.name}</h3>
                    <p className="text-sm text-white/80 mb-4">{service.description}</p>
                    <div className="inline-flex items-center space-x-2 text-white font-bold text-sm">
                      <span>{t('common.explore')}</span>
                      <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="text-center">
              <div className="text-5xl font-black text-[#6BBE45] mb-2">15+</div>
              <div className="text-sm tracking-[0.2em] uppercase text-gray-600">{t('home.yearsExperience')}</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-[#FF3B3B] mb-2">50K+</div>
              <div className="text-sm tracking-[0.2em] uppercase text-gray-600">{t('home.satisfiedClients')}</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-[#6BBE45] mb-2">{services.length || 5}</div>
              <div className="text-sm tracking-[0.2em] uppercase text-gray-600">{t('home.activeBusinesses')}</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-[#FF3B3B] mb-2">50+</div>
              <div className="text-sm tracking-[0.2em] uppercase text-gray-600">{t('home.countriesServed')}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
